import React, {
	useCallback,
	useRef,
	useEffect,
} from 'react';

import {
	vultureENV,
} from 'Vulture/ENV';

import CustomIcon from '../../Common/CustomIcon/CustomIcon';
import PdfPreview from '../../Common/PdfPreview/PdfPreview';

import style from './ChonkyFileBrowser.module.css';

import {
	formatBytes,
	getFilePath,
	getThumbnailPath,
	isDocumentFile,
	isPreviewable,
	VALID_FORMATS,
} from './lib';
import { isSystemFolder } from '../../../../Util/ChonkyFileBrowserETL';
import Img from 'wrapper/Img';
import { s3PublicURL } from 'Vulture/S3.js';

const isImageExt = (fileName) => {
	const ext = (fileName || '').split('.').pop().toLowerCase();
	return [ 'jpg', 'jpeg', 'png', 'webp', ].includes(ext);
};

const ChonkyCustomFileList = (props) => {
	const {
		app,
		accountUUID,
		isListView,
		isInjected,
		hideActionsColumn,
		resolvedInjectedColumns,
		readOnly,

		files,
		selectedFileIds,
		handleCheckboxChange,
		handleDragStart,
		handleDragEnd,
		handleDragOver,
		handleDragLeave,
		handleDrop,
		handleSort,
		handleDelete,
		renderSortArrow,
		renderFileIcon,
		dropTarget,
		dragOverIndex,
		onInjectedReorder,
		onInjectedReorderProgress,
		setCurrentFolderId,
		onInjectedFileOpen,
		fileBrowserRef,
		setSelectedFileIds,
		mode,
		onSelectImage,
		onSelectFolder,
		allowedFormats = VALID_FORMATS,
		setSearchQuery,
		hideEdit,
		hideEditAndDeleteButtons,
		onInjectedEdit,
		onInjectedDelete,
		onInjectedCalendar,
		draggingFileId,
		getDisplayPath,
		isRoot,
		hasWriteAccess,
		hasDeleteAccess,
		openEditModal,
		handleDownloadFile,
		columnWidths = {},
		setColumnWidth = () => {},
		columnVisibility = {},
		toggleColumnVisibility = () => {},
	} = props;

	// In picker (select) modes, hide files whose extension isn't allowed for the
	// context (e.g. only PDFs in the PDF picker). Library/injected modes show all.
	const isSelectMode = mode === 'select-image' || mode === 'select-pdf';
	const visibleFiles = isSelectMode
		? files.filter((f) => {
			return f.isDir || allowedFormats.includes((f.name || '').split('.').pop().toLowerCase());
		})
		: files;

	const resizingRef = useRef({
		active: false,
		colId: null,
		startX: 0,
		startWidth: 0,
	});

	useEffect(() => {
		const onMouseMove = (e) => {
			if (!resizingRef.current.active) return;
			const dx = e.clientX - resizingRef.current.startX;
			const newWidthPx = Math.max(40, (parseInt(resizingRef.current.startWidth, 10) || 100) + dx);
			setColumnWidth(resizingRef.current.colId, `${newWidthPx}px`);
		};
		const onMouseUp = () => {
			if (!resizingRef.current.active) return;
			resizingRef.current.active = false;
			resizingRef.current.colId = null;
		};
		window.addEventListener('mousemove', onMouseMove);
		window.addEventListener('mouseup', onMouseUp);
		return () => {
			window.removeEventListener('mousemove', onMouseMove);
			window.removeEventListener('mouseup', onMouseUp);
		};
	}, [ setColumnWidth ]);

	const onResizerMouseDown = (e, colId) => {
		e.preventDefault();
		resizingRef.current.active = true;
		resizingRef.current.colId = colId;
		resizingRef.current.startX = e.clientX;
		resizingRef.current.startWidth = columnWidths[colId] || e.target.parentElement.offsetWidth || 100;
	};

	const headerCellStyle = (colId, baseStyle = {}) => {
		const width = columnWidths && columnWidths[colId];
		if (width) {
			return { ...baseStyle, width, minWidth: 40, maxWidth: '60%' };
		}
		return baseStyle;
	};

	const openPreviewModal = useCallback((file) => {
		if (!file || file.isDir) return;

		if (isDocumentFile(file.name)) {
			app.openModal({
				modalSize:  3,
				showModal:  true,
				modalProps: {
					customClass: 'pdf-preview-content',
					title:       `Preview: ${file.name}`,
					jsx:         (
						<div
							style={{
								width:    '100%',
								height:   '80vh',
								overflow: 'hidden',
							}}
						>
							<PdfPreview
								fileUrl={getFilePath(file.path)}
								accountUUID={accountUUID}
								label={file.name}
							/>
						</div>
					),
				},
			});
			return;
		}

		if (!isImageExt(file.name)) return;

		const fullUrl = s3PublicURL('ts-condor-assets', `${vultureENV}/ts-library/${file.path}`);
		app.openModal({
			modalSize:  3,
			showModal:  true,
			modalProps: {
				title: `Preview: ${file.name}`,
				jsx:   (
					<div
						style={{
							width:          '90vw',
							height:         '80vh',
							display:        'flex',
							alignItems:     'center',
							justifyContent: 'center',
							overflow:       'hidden',
						}}
					>
						<Img
							src={fullUrl}
							data-test-id="imagePreviewImage"
							alt={file.name}
							style={{
								width:     '100%',
								height:    '100%',
								objectFit: 'contain',
							}}
						/>
					</div>
				),
			},
		});
	}, [ app, accountUUID, ]);

	const canPreview = useCallback((file) => {
		return isPreviewable(file) && (!isDocumentFile(file.name) || Boolean(accountUUID));
	}, [ accountUUID, ]);

	if (isListView) {
		return (
			<table className={style.customFileListTable} data-test-id="customFileListTable">
				<thead className={style.customFileListThead}>
					<tr>
						{isInjected ? (
							<>
								<th className={`${style.customFileListTh} ${style.headerDivider}`} style={{ width: 40 }}>
									{/* Checkbox column header */}
								</th>
								{resolvedInjectedColumns.map((col) => {
									const isSortable = !col.notSortable;
									if (columnVisibility && col.id && columnVisibility[col.id] === false) return null;
									return (
										<th
											key={col.id}
											data-test-id={`columnHeader-${col.id}`}
											className={`${style.customFileListTh} ${style.headerDivider}`}
											style={{
												...headerCellStyle(col.id),
												textAlign: (col.center || col.centerColumn) ? 'center' : 'left',
												...(col.width && { width: col.width }),
												cursor: isSortable ? 'pointer' : 'default',
												position: 'relative',
												// Remove left padding only for the drag handle column so its icon centers exactly
												...(col.id === 'drag_handle' ? { paddingLeft: 0 } : {}),
											}}
											onClick={isSortable ? () => {
												handleSort(col.id);
											} : undefined}
										>
											{col.label} {isSortable && renderSortArrow(col.id)}
											<div
												onMouseDown={(e) => onResizerMouseDown(e, col.id)}
												className={style.headerResizer}
												role="separator"
												aria-orientation="vertical"
											/>
										</th>
									);
								})}
								{!hideActionsColumn && <th data-test-id="columnHeader-actions" className={style.customFileListTh} style={{
									width: 100,
									textAlign: 'center',
									padding: '0 0.5rem',
								}}>Actions</th>}
							</>
						) : (
							<>
								{!isRoot && !readOnly && (
									<th className={`${style.customFileListTh} ${style.headerDivider}`} style={{ width: 40 }}>
									</th>
								)}
								{(columnVisibility.order !== false) && (
									<th
										data-test-id="columnHeader-order"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('order');
										}}
										style={{
											...headerCellStyle('order', { width: '9rem', textAlign: 'center' }),
											position: 'relative',
										}}
									>
										Order {renderSortArrow('order')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'order')} className={style.headerResizer} />
									</th>
								)}
								{(columnVisibility.name !== false) && (
									<th
										data-test-id="columnHeader-name"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('name');
										}}
										style={{
											...headerCellStyle('name', { textAlign: 'left' }),
											cursor:     'pointer',
											userSelect: 'none',
											position: 'relative',
										}}
									>
										Name {renderSortArrow('name')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'name')} className={style.headerResizer} />
									</th>
								)}
								{(columnVisibility.description !== false) && (
									<th
										data-test-id="columnHeader-description"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('description');
										}}
										style={{
											...headerCellStyle('description'),
											cursor:     'pointer',
											userSelect: 'none',
											position: 'relative',
										}}
									>
										Descriptive Name {renderSortArrow('description')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'description')} className={style.headerResizer} />
									</th>
								)}
								{(columnVisibility.size !== false) && (
									<th
										data-test-id="columnHeader-size"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('size');
										}}
										style={{
											...headerCellStyle('size', { textAlign: 'center' }),
											cursor:     'pointer',
											userSelect: 'none',
											position: 'relative',
										}}
									>
										Size {renderSortArrow('size')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'size')} className={style.headerResizer} />
									</th>
								)}
								{(columnVisibility.resolution !== false) && (
									<th
										data-test-id="columnHeader-resolution"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('resolution');
										}}
										style={{
											...headerCellStyle('resolution', { textAlign: 'center' }),
											cursor:     'pointer',
											userSelect: 'none',
											position: 'relative',
										}}
									>
										Resolution {renderSortArrow('resolution')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'resolution')} className={style.headerResizer} />
									</th>
								)}
								{(columnVisibility.path !== false) && (
									<th
										data-test-id="columnHeader-path"
										className={`${style.customFileListTh} ${style.headerDivider}`}
										onClick={() => {
											handleSort('path');
										}}
										style={{
											...headerCellStyle('path'),
											cursor:     'pointer',
											userSelect: 'none',
											position: 'relative',
										}}
									>
										Path {renderSortArrow('path')}
										<div onMouseDown={(e) => onResizerMouseDown(e, 'path')} className={style.headerResizer} />
									</th>
								)}
								{!readOnly && <th data-test-id="columnHeader-actions" className={`${style.customFileListTh} ${style.headerDivider}`} style={{ width: 100, textAlign: 'center' }}></th>}
							</>
						)}
					</tr>
				</thead>
				<tbody className={style.customFileListTbody}>
					{visibleFiles.map((file, index) => {
						const FileIcon = () => {
							return renderFileIcon(file, 28);
						};
						const isSelected = selectedFileIds.has(file.id);
						const isDragTarget = dropTarget === file.id;
						const isDragOverTarget = dragOverIndex === index;
						let isInactive = false;
						// Use isInactive from file if provided (e.g., from SlideDeckTable)
						if (typeof file.isInactive === 'boolean') {
							isInactive = file.isInactive;
						} else {
							// Fallback to start_date/end_date logic
							try {
								if (file.start_date && file.end_date) {
									const now = new Date();
									const start = new Date(file.start_date);
									const end = new Date(file.end_date);
									if (!isNaN(start.getTime()) && !isNaN(end.getTime())) {
										const endInclusive = new Date(end);
										endInclusive.setHours(23, 59, 59, 999);
										if (now < start || now > endInclusive) {
											isInactive = true;
										}
									}
								}
							} catch (e) {/* ignore parse issues */}
						}

						return (
							<tr
								key={file.id}
								className={`${style.customFileListTr}${isSelected ? ' ' + style.selected : ''}${isDragTarget ? ' ' + style.dragTarget : ''}${isDragOverTarget ? ' ' + style.dragOver : ''}${draggingFileId === file.id ? ' ' + style.draggingOrigin : ''}${isInactive ? ' ' + style.inactiveRow : ''}`}
								draggable={isInjected ? ((onInjectedReorder || onInjectedReorderProgress) && !readOnly) : (!readOnly)}
								onDragStart={(e) => {
									handleDragStart(e, file);
								}}
								onDragEnd={(e) => {
									handleDragEnd(e, file);
								}}
								onDragOver={(e) => {
									handleDragOver(e, file, index);
								}}
								onDragLeave={(e) => {
									handleDragLeave(e, file);
								}}
								onDrop={(e) => {
									handleDrop(e, file, index);
								}}
								onClick={(e) => {
									if (isInjected) {
										if (file.isDir) {
											setCurrentFolderId(file.id);
										} else if (onInjectedFileOpen) {
											onInjectedFileOpen(file);
										}
										// Honor the per-file selectable flag so
										// non-selectable rows (e.g. Loop Assignments
										// parent headers, non-VCL children) don't
										// become selected via a stray row click.
										if (file.selectable !== false) {
											const newSet = new Set([ file.id, ]);
											fileBrowserRef.current.setFileSelection(newSet);
											setSelectedFileIds(newSet);
										}
										return;
									}
									if (isSelectMode && !file.isDir) {
										const ext = (file.name || '').split('.').pop().toLowerCase();
										if (allowedFormats.includes(ext) && onSelectImage) {
											onSelectImage({
												fileID:      file.id,
												previewURL:  getThumbnailPath(file.path),
												imgURL:	     getFilePath(file.path),
												name:        file.name,
												imgKey:	     file.path,
												size:        file.size,
												resolution:  file.resolution || '-',
												description: file.description || file.name,
												duration:    file.duration || 0,
											});
											return;
										}
									}
									if (file.isDir && e.target.type !== 'checkbox') {
										setSearchQuery('');
										setCurrentFolderId(file.id);
									} else if (!file.isDir && e.target.type !== 'checkbox' && !readOnly && file.selectable !== false) {
										handleCheckboxChange(file, !isSelected);
									}
								}}
							>
								{!isInjected && (
									<>
										{!isRoot && !readOnly &&
											(isSystemFolder(file) || columnVisibility.order === false ? (
												<td className={style.customFileListTd} style={{ width: 40 }} />
											) : (
											<td className={style.customFileListTd} style={{ textAlign: 'center' }}>
												<input
													type="checkbox"
													checked={isSelected}
													onChange={(e) => {
														handleCheckboxChange(file, e.target.checked);
													}}
													onClick={(e) => {
														e.stopPropagation();
													}}
													className={style.checkboxLarge}
												/>
											</td>
											))
										}
										{(columnVisibility.order !== false) && (
											<td className={`${style.customFileListTd} ${style.order}`} style={{ textAlign: 'center', width: columnWidths.order }}>
												{file.order}
											</td>
										)}
										{(columnVisibility.name !== false) && (
											<td
												className={style.customFileListTd}
												style={{ width: columnWidths.name, display: 'flex', alignItems: 'center', gap: '0.5rem', textAlign: 'left' }}
											>
												{file.parentId !== 'root' && (
													<>
														{(file.isDir && onSelectFolder &&
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	onSelectFolder(file);
																}}
																className={`button ${style.selectButtonListView}`}
															>
																<CustomIcon icon='plus' />
															</button>)}
														{(!file.isDir && onSelectImage &&
															<button
																onClick={(e) => {
																	e.stopPropagation();
																	onSelectImage({
																		fileID:      file.id,
																		previewURL:  getThumbnailPath(file.path),
																		imgURL:	     getFilePath(file.path),
																		name:        file.name,
																		imgKey:	     file.path,
																		size:        file.size,
																		resolution:  file.resolution || '-',
																		description: file.description || file.name,
																		duration:    file.duration || 0,
																	});
																}}
																className={`button ${style.selectButtonListView}`}
															>
																<CustomIcon icon='plus' />
															</button>)}
													</>
												)}
												<span className={style.folderIconContainer} style={{ display: 'inline-flex' }}>
													<FileIcon />
												</span>
												<span style={{ flex: 1, textAlign: 'left' }}>{file.name}</span>
											</td>
										)}
									</>
								)}
								{isInjected ? (
									<>
										{isSystemFolder(file) ? (
											<td className={style.customFileListTd} style={{ textAlign: 'center' }} />
										) : (
										<td className={style.customFileListTd} style={{ textAlign: 'center' }}>
											{file.selectable !== false && (
												<input
													type="checkbox"
													checked={isSelected}
													onChange={(e) => {
														e.stopPropagation();
														handleCheckboxChange(file, e.target.checked);
													}}
													onClick={(e) => {
														e.stopPropagation();
													}}
													className={style.checkboxLarge}
												/>
											)}
										</td>
										)}
										{resolvedInjectedColumns.map((col) => {
											if (columnVisibility && col.id && columnVisibility[col.id] === false) return null;
											const val = typeof col.accessor === 'function' ? col.accessor(file) : file[col.id];
											return <td
                                                key={col.id}
                                                className={style.customFileListTd}
                                                style={{
                                                    textAlign: (col.center || col.centerColumn) ? 'center' : 'left',
                                                    // Remove left padding only for the drag handle column so its icon aligns center
                                                    ...(col.id === 'drag_handle' ? { paddingLeft: 0 } : {}),
                                                }}
                                            >{val}</td>;
										})}
										{!hideActionsColumn && <td className={`${style.customFileListTd} ${style.icon}`} style={{ width: 100, textAlign: 'center', padding: '0 0.5rem' }}>
											<div className={style.actionBtnRow} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
												{/* Edit */}
												{!hideEdit && !hideEditAndDeleteButtons && <button
													className={style.customGridActionBtn}
													data-test-id="rowEditButton"
													title="Edit"
													onClick={(e) => {
														e.stopPropagation();
														if (onInjectedEdit) onInjectedEdit(file);
													}}
												>
													<CustomIcon icon="edit" />
												</button>}
												{/* In-Folder Scheduling calendar — only for folder rows */}
												{onInjectedCalendar && file?.raw?.kind === 'folder' && <button
													type="button"
													className={style.customGridActionBtn}
													title="In-Folder Scheduling"
													aria-label="In-Folder Scheduling"
													onClick={(e) => {
														e.stopPropagation();
														onInjectedCalendar(file);
													}}
												>
													<CustomIcon icon="calendar-alt" />
												</button>}
												{/* Delete */}
												{!hideEditAndDeleteButtons && <button
													className={style.customGridActionBtn}
													data-test-id="rowDeleteButton"
													title="Delete"
													onClick={(e) => {
														e.stopPropagation();
														if (onInjectedDelete) onInjectedDelete(file);
													}}
												>
													<CustomIcon icon="trash" />
												</button>}
											</div>
										</td>}
									</>
								) : (
									<>
										{(columnVisibility.description !== false) && <td className={`${style.customFileListTd} ${style.description}`} style={{ width: columnWidths.description }}>{file.description || '-'}</td>}
										{(columnVisibility.size !== false) && <td className={`${style.customFileListTd} ${style.size}`} style={{ width: columnWidths.size, textAlign: 'center' }}>{formatBytes(file.size)}</td>}
										{(columnVisibility.resolution !== false) && <td className={`${style.customFileListTd} ${style.resolution}`} data-test-id="resolution" style={{ width: columnWidths.resolution, textAlign: 'center' }}>{file.resolution || '-'}</td>}
										{(columnVisibility.path !== false) && <td className={`${style.customFileListTd} ${style.filePath}`} style={{ width: columnWidths.path }}>{getDisplayPath(file)}</td>}
									</>
								)}
								{!readOnly && !isInjected && (
									<td className={`${style.customFileListTd} ${style.lastFileListTd}`} style={{ width: 100, textAlign: 'center', padding: '0 0.5rem' }}>
										<div className={style.actionBtnRow} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem' }}>
											{hasWriteAccess && !isSystemFolder(file) &&
												(<button
													className={style.customGridActionBtn}
													data-test-id="rowEditButton"
													title="Edit"
													onClick={(e) => {
														e.stopPropagation();
														openEditModal(file);
													}}
												>
													<CustomIcon icon="edit" />
												</button>)
											}
											{canPreview(file) && (
												<button
													className={style.customGridActionBtn}
													data-test-id="filePreviewButton"
													title="Preview"
													onClick={(e) => {
														e.stopPropagation();
														openPreviewModal(file);
													}}
												>
													<CustomIcon icon="search" />
												</button>
											)}
											{!file.isDir && (
												<button
													className={style.customGridActionBtn}
													title="Download"
													onClick={async (e) => {
														e.stopPropagation();
														await handleDownloadFile(file);
													}}
												>
													<CustomIcon icon="download" />
												</button>
											)}
											{hasDeleteAccess && !isSystemFolder(file) &&
												(<button
													className={style.customGridActionBtn}
													data-test-id="rowDeleteButton"
													title="Delete"
													onClick={(e) => {
														handleDelete(e, file);
													}}
												>
													<CustomIcon icon="trash" />
												</button>)
											}
										</div>
									</td>
								)}
							</tr>
						);
					})}
				</tbody>
			</table>
		);
	}
	return (
		<div className={style.customGridView} data-test-id="customGridView">
			{visibleFiles.map((file, index) => {
				const FileIcon = () => {
					return renderFileIcon(file, 96);
				};
				const isSelected = selectedFileIds.has(file.id);
				const isDragTarget = dropTarget === file.id;
				const isDragOverTarget = dragOverIndex === index;

				const handleEdit = (e) => {
					e.stopPropagation();
					openEditModal(file);
				};
				const handleDownload = async (e) => {
					e.stopPropagation();
					await handleDownloadFile(file);
				};
				const handlePreview = (e) => {
					e.stopPropagation();
					openPreviewModal(file);
				};

				return (
					<div
						key={file.id}
						className={`${style.customGridItem} ${isSelected ? style.selected : ''} ${isDragTarget ? style.dragTarget : ''} ${isDragOverTarget ? style.dragOver : ''} ${draggingFileId === file.id ? style.draggingOrigin : ''}`}
						data-test-id="customGridItem"
						draggable={isInjected ? (onInjectedReorder && !readOnly) : (!readOnly)}
						onDragStart={(e) => {
							handleDragStart(e, file);
						}}
						onDragEnd={(e) => {
							handleDragEnd(e, file);
						}}
						onDragOver={(e) => {
							handleDragOver(e, file, index);
						}}
						onDragLeave={(e) => {
							handleDragLeave(e, file);
						}}
						onDrop={(e) => {
							handleDrop(e, file, index);
						}}
						onClick={(e) => {
							if (isInjected) {
								if (file.isDir) {
									setCurrentFolderId(file.id);
								} else if (onInjectedFileOpen) {
									onInjectedFileOpen(file);
								}
								const newSet = new Set([ file.id, ]);
								fileBrowserRef.current.setFileSelection(newSet);
								setSelectedFileIds(newSet);
								return;
							}
							if (isSelectMode && !file.isDir) {
								const ext = (file.name || '').split('.').pop().toLowerCase();
								if (allowedFormats.includes(ext) && onSelectImage) {
									onSelectImage({
										fileID:      file.id,
										previewURL:  getThumbnailPath(file.path),
										imgURL:      getFilePath(file.path),
										name:        file.name,
										imgKey:      file.path,
										size:        file.size,
										resolution:  file.resolution || '-',
										description: file.description || file.name,
										duration:    file.duration || 0,
									});
									return;
								}
							}
							if (file.isDir && e.target.type !== 'checkbox') {
								setSearchQuery('');
								setCurrentFolderId(file.id);
							} else if (!file.isDir && e.target.type !== 'checkbox' && !readOnly && file.selectable !== false) {
								handleCheckboxChange(file, !isSelected);
							}
						}}
						title={
							`Name: ${file.name}\n` +
							`Size: ${formatBytes(file.size)}\n` +
							`Resolution: ${file.resolution || '-'}\n` +
							`Description: ${file.description || '-'}`
						}
						style={{
							cursor: file.isDir ? 'pointer' : 'default',
						}}
					>
						{(!readOnly && !isRoot && !isSystemFolder(file)) && (
							<div className={style.customGridActions}>
								{hasWriteAccess && (<button className={style.customGridActionBtn} title="Edit" onClick={handleEdit}>
									<CustomIcon icon="edit" />
								</button>)}
								{canPreview(file) && (
									<button className={style.customGridActionBtn} data-test-id="filePreviewButton" title="Preview" onClick={handlePreview}>
										<CustomIcon icon="search" />
									</button>
								)}
								{!file.isDir && (
									<button className={style.customGridActionBtn} title="Download" onClick={handleDownload}>
										<CustomIcon icon="download" />
									</button>
								)}
								{hasDeleteAccess && (<button className={style.customGridActionBtn} data-test-id="rowDeleteButton" title="Delete" onClick={(e) => {
									handleDelete(e, file);
								}}>
									<CustomIcon icon="trash" />
								</button>)}
							</div>
						)}
						<div className={style.customGridIcon} data-test-id="customGridIcon"><FileIcon /></div>
						<div className={style.customGridName} data-test-id="customGridName">{file.name}</div>
						{(!readOnly && !isRoot && !isSystemFolder(file)) && (
							<input
								type="checkbox"
								checked={isSelected}
								onChange={(e) => {
									handleCheckboxChange(file, e.target.checked);
								}}
								onClick={(e) => {
									e.stopPropagation();
								}}
								className={style.gridCheckbox}
							/>
						)}
						{file.parentId !== 'root' && (
							<>
								{(file.isDir && onSelectFolder &&
									<button
										onClick={(e) => {
											e.stopPropagation();
											onSelectFolder(file);
										}}
										className={`button ${style.selectButton}`}
									>
										<CustomIcon icon='plus' />
									</button>)}
								{(!file.isDir && onSelectImage &&
									<button
										onClick={(e) => {
											e.stopPropagation();
											onSelectImage({
												fileID:      file.id,
												previewURL:  getThumbnailPath(file.path),
												imgURL:	     getFilePath(file.path),
												name:        file.name,
												imgKey:	     file.path,
												size:        file.size,
												resolution:  file.resolution || '-',
												description: file.description || file.name,
												duration:    file.duration || 0,
											});
										}}
										className={`button ${style.selectButton}`}
									>
										<CustomIcon icon='plus' />
									</button>)}
							</>
						)}
					</div>
				);
			})}
		</div>
	);
};

export default ChonkyCustomFileList;